import { useTranslation } from "react-i18next";
import { CustomerBaseDto, CustomerSearchFilters, ICustomer } from "../../types";
import { useCallback, useMemo } from "react";
import { ColDef, ICellRendererParams, IServerSideDatasource, RowClickedEvent } from "ag-grid-community";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material/index";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { findCustomerBaseByUniqueDataAsync, searchCustomerByFiltersAsync } from "../../api/service/customerbaseApi";
import { Close } from "@mui/icons-material";

interface CBDialogSearchCustomerProps {
    open: boolean,
    onClose: () => void,
    onCustomerSelected: (selectedCustomer: CustomerBaseDto) => void,
    customerFilters: CustomerSearchFilters | null,
    customerData?: any[],
    isPerson: boolean
}

const CBDialogSearchCustomer = (props: CBDialogSearchCustomerProps) => {

    const { t } = useTranslation("customerbase");

    const columnDefs = useMemo<ColDef[]>(() => {
        if(props.isPerson) {
            
            return [
                {
                    field: 'registry.firstName',
                    headerName: t('firstName'),
                    width: 135
                },
                {
                    field: 'registry.lastName',
                    headerName: t('lastName'),
                    width: 135
                },
                {
                    field: 'registry.taxCode',
                    headerName: t('taxCode'),
                    width: 140
                },
                {
                    field: 'registry.drvLicenceNumber',
                    headerName: t('drvLicenceNumber'),
                    width: 140
                },
                {
                    field: 'registry.birthDate',
                    headerName: t('birthDate'),
                    width: 130
                },
                {
                    field: 'registry.birthProvince',
                    headerName: t('birthProvince'),
                    width: 140
                },
                {
                    field: 'email',
                    headerName: t('email'),
                    width: 200
                },
                {
                    field: 'contactInfo.mobile',
                    headerName: t('mobile'),
                    width: 110
                }
            ]
        }
        return [
            {
                field: 'registry.businessName',
                headerName: t('businessName'),
                width: 140
            },
            {
                field: 'registry.taxCode',
                headerName: t('taxCode'),
                width: 140
            },
            {
                field: 'registry.vatNumber',
                headerName: t('vatNumber'),
                width: 140,
                cellRenderer: (params: ICellRendererParams) => {
                    //console.log(params);
                    return !!params?.data?.registry?.vatNumberCountry && !!params?.data?.registry?.vatNumber ? params?.data?.registry?.vatNumberCountry + params?.data?.registry?.vatNumber : params?.data?.registry?.vatNumber || '';
                }
            },
            {
                field: 'email',
                headerName: t('email'),
                width: 200
            },
            {
                field: 'contactInfo.mobile',
                headerName: t('mobile'),
                width: 110
            }
        ];

    }, [t, props.isPerson]);

    const defaultColDef = useMemo<ColDef>(() => ({
        suppressMovable: true,
        suppressMenu: true,
        resizable: true
    }), []);

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {

            if(!!props.customerData) return;
            try {
                var data: any[] = [];
                var total: number = 0;

                if(props.customerFilters){
                    if(!!props.customerFilters.taxCode || !!props.customerFilters.drvLicenceNumber) {
                        //const res = (await getCustomerBaseByUniqueIdAsync(props.customerFilters.taxCode || null, props.customerFilters.drvLicenceNumber || null, props.customerFilters.partnerType || null)).data;
                        const res = (await findCustomerBaseByUniqueDataAsync({taxCode: props.customerFilters.taxCode || null, drvLicenceNumber: props.customerFilters.drvLicenceNumber || null, partnerType: props.customerFilters.partnerType || null})).data;
                        data = !!res ? [res] : [];
                        total = data.length;

                    } else {
                        const start = params.request.startRow;
                        const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                        const resp = (await searchCustomerByFiltersAsync({
                            filters: props.customerFilters,
                            orderBy: "SURNAME",
                            orderDir: "ASC",
                            start: start,
                            limit: limit
                        }));

                        data = resp.data;
                        total = (isNaN(Number(resp.customFields.size)) ? data.length : Number(resp.customFields.size));
                    }
                }

                params.success({rowCount: total, rowData: data});
                total == 0 && params.api.showNoRowsOverlay();

            } catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [props.customerFilters, props.customerData]);

    const onRowClicked = useCallback((event: RowClickedEvent) => {
        props.onCustomerSelected(event.data);
        props.onClose();
    }, [props.onCustomerSelected]);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="lg"
        >
            <IconButton
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 12
                }}
                onClick={props.onClose}
            >
              <Close fontSize="inherit"/>  
            </IconButton>
            <DialogTitle>
                {t("labels.customers")}
            </DialogTitle>
            <DialogContent>
                {!!props.customerData &&
                    <AgGridCustomTable
                        rowData={props.customerData}
                        height="550px"
                        rowModelType="clientSide"
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        rowStyle={{
                            cursor: 'pointer'
                        }}
                        onRowClicked={onRowClicked}
                        pagination={false}
                    /> 
                }
                {!props.customerData && 
                    <AgGridCustomTable
                        height="550px"
                        rowModelType="serverSide"
                        serverSideDatasource={serverSideDatasource}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        rowStyle={{
                            cursor: 'pointer'
                        }}
                        onRowClicked={onRowClicked}
                    />
                }
            </DialogContent>

        </Dialog>
    )

}

export default CBDialogSearchCustomer;